import { useRef, useState, useCallback, useEffect } from 'react';
import type { Message } from '../types';

interface UseScrollManagementOptions {
  messages: Message[];
  streamingMessage: Message | null;
  activeSessionId: string | null;
  minimized: boolean;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/** Downgrade smooth scrolling to instant when the user asked for reduced motion. */
function resolveScrollBehavior(requested: ScrollBehavior): ScrollBehavior {
  return requested === 'smooth' && prefersReducedMotion() ? 'auto' : requested;
}

interface UseScrollManagementReturn {
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottomVisible: boolean;
  newMessagesWhileScrolledUp: number;
  handleMessagesScroll: () => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

export function useScrollManagement({
  messages,
  streamingMessage,
  activeSessionId,
  minimized,
}: UseScrollManagementOptions): UseScrollManagementReturn {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);
  const [scrollToBottomVisible, setScrollToBottomVisible] = useState(false);
  const [newMessagesWhileScrolledUp, setNewMessagesWhileScrolledUp] = useState(0);
  const atBottomStateRef = useRef(true);
  const lastMessageCountRef = useRef(0);

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    const atBottom = distance < 80;
    autoScrollRef.current = atBottom;
    if (atBottomStateRef.current !== atBottom) {
      atBottomStateRef.current = atBottom;
      setScrollToBottomVisible(!atBottom);
      if (atBottom) setNewMessagesWhileScrolledUp(0);
    }
  }, []);

  const scrollToBottom = useCallback((requestedBehavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const behavior = resolveScrollBehavior(requestedBehavior);
    autoScrollRef.current = true;
    atBottomStateRef.current = true;
    setScrollToBottomVisible(false);
    setNewMessagesWhileScrolledUp(0);

    const top = container.scrollHeight;
    try {
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top, behavior });
      } else {
        container.scrollTop = top;
      }
    } catch {
      container.scrollTop = top;
    }
  }, []);

  // Auto-scroll on new messages / streaming
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !autoScrollRef.current) return;
    const behavior: ScrollBehavior = streamingMessage ? 'auto' : resolveScrollBehavior('smooth');
    const top = container.scrollHeight;
    try {
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top, behavior });
      } else {
        container.scrollTop = top;
      }
    } catch {
      container.scrollTop = top;
    }
  }, [messages, streamingMessage]);

  // Reset scroll on session change
  useEffect(() => {
    autoScrollRef.current = true;
    atBottomStateRef.current = true;
    setScrollToBottomVisible(false);
    setNewMessagesWhileScrolledUp(0);
    lastMessageCountRef.current = 0;

    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [activeSessionId]);

  // Track new messages while scrolled up
  useEffect(() => {
    const count = messages.length;
    const prevCount = lastMessageCountRef.current;
    lastMessageCountRef.current = count;

    if (count > prevCount && !autoScrollRef.current) {
      setNewMessagesWhileScrolledUp(n => n + (count - prevCount));
      setScrollToBottomVisible(true);
    }
  }, [messages.length]);

  // Reset scroll when widget opens
  useEffect(() => {
    if (minimized) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    autoScrollRef.current = true;
    atBottomStateRef.current = true;
    setScrollToBottomVisible(false);
    setNewMessagesWhileScrolledUp(0);
    container.scrollTop = container.scrollHeight;
  }, [minimized]);

  return {
    messagesEndRef,
    messagesContainerRef,
    scrollToBottomVisible,
    newMessagesWhileScrolledUp,
    handleMessagesScroll,
    scrollToBottom,
  };
}
